import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';

import { MatSnackBar } from '@angular/material/snack-bar';

import { Observable, of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { checkedMinMaxDateValidator } from '@core/validators/checked-min-max-date-validator';

import { PlanAlloc } from '@core/models/plan-alloc/plan-alloc.model';
import { FilteredCampaign, FilteredChannel } from '@core/models/plan-filter/plan-filter.model';

import { ScreenService } from '@core/services/screen/screen.service';

import { ScreenTypeComponent } from '@shared/dialog/screen-dialog/screen-type/screen-type.component';

const rawFormats: any[] = [
  { id: 1, duration: 20, weight: 0, visibility: false },
  { id: 2, duration: 20, weight: 0, visibility: false },
  { id: 3, duration: 20, weight: 0, visibility: false },
  { id: 4, duration: 20, weight: 0, visibility: false },
  { id: 5, duration: 20, weight: 0, visibility: false },
  { id: 6, duration: 20, weight: 0, visibility: false },
];

const rawDayParts: any[] = [
  { code: 'DA6', lib: 'Day', weight: 80 },
  { code: 'AC6', lib: 'Access', weight: 20 },
  { code: 'A26', lib: 'Après 20h', weight: 0 },
];

const rawChannels: FilteredChannel[] = [
  { id: 2, name: 'FRANCE 2', areaId: 'NAT', visibility: true },
  { id: 3, name: 'FRANCE 3', areaId: 'NAT', visibility: true },
  { id: 5, name: 'FRANCE 5', areaId: 'NAT', visibility: true },
];

const rawChannelBudgets: any[] = [
  { id: 2, budget: 0 },
  { id: 3, budget: 0 },
  { id: 5, budget: 0 },
];

@Component({
  standalone: false,
  selector: 'app-tv-plan-valuation-dialog',
  templateUrl: './tv-plan-valuation-dialog.component.html',
  styleUrls: ['./tv-plan-valuation-dialog.component.scss'],
})
export class TvPlanValuationDialogComponent implements OnInit, OnDestroy {
  @Input() planForm: UntypedFormGroup;

  logoChannelPath = 'assets/images/logo_channel/';

  MAX_WAVES_COUNT = 6;

  FILTER_IN_LABEL = 'Uniquement';
  FILTER_OUT_LABEL = 'Tout sauf';

  planAlloc: PlanAlloc;
  hasUnsavedSpots: boolean;

  filteredCampaigns: Observable<FilteredCampaign[]>;
  startCampaignDate: any;
  endCampaignDate: any;

  rawWaves = [];
  screenTypes = [];
  selectedTypes = [];
  screenTypeToDelete = [];
  screenTypeToInsert = [];
  typeLogic = this.FILTER_IN_LABEL;

  getLogoChannelPath(id: any): string {
    return `${this.logoChannelPath}${id}.png`;
  }

  get IsMainPeriodAWave(): boolean {
    if (this.startCampaignDate?.format('YYYY-MM-DD') != this.planForm?.value?.startDate?.format('YYYY-MM-DD')) {
      return true;
    }
    if (this.endCampaignDate?.format('YYYY-MM-DD') != this.planForm?.value?.endDate?.format('YYYY-MM-DD')) {
      return true;
    }
    return false;
  }

  get selectedCampaign(): any {
    return this.planForm.value.campaign;
  }

  get selectedStartDate(): Date {
    return this.planForm.value.startDate;
  }

  get selectedEndDate(): Date {
    return this.planForm.value.endDate;
  }

  get selectedWaves(): any[] {
    let result = [];
    for (let idx in this.rawWaves) {
      const sd = this.planForm.value['waveStartDate' + idx];
      const ed = this.planForm.value['waveEndDate' + idx];
      const item = {
        startDate: sd,
        endDate: ed,
      };
      result = [...result, item];
    }

    return result;
  }

  get selectedFormats(): any[] {
    return rawFormats.filter(f => f.visibility).map(f => f.id - 1);
  }

  get selectedDurations(): number[] {
    return this.selectedFormats.map(id => this.planForm.value['format' + id]);
  }

  get selectedDurationsWeight(): number[] {
    return this.selectedFormats.map(id => this.planForm.value['formatWeight' + id]);
  }

  get allDayParts(): any[] {
    return rawDayParts;
  }

  get selectedDayParts(): any[] {
    return rawDayParts.filter(d => this.planForm.value['dayPart' + d.code] > 0).map(d => d.code);
  }

  get selectedDayPartsCode(): any[] {
    return this.selectedDayParts.map(code => code);
  }

  get selectedDayPartsWeight(): any[] {
    return this.selectedDayParts.map(code => this.planForm.value['dayPart' + code]);
  }

  get selectedFilterIn(): any[] {
    if (this.typeLogic == this.FILTER_OUT_LABEL) return [];
    return this.selectedTypes.map(t => t.codevt);
  }

  get selectedFilterOut(): any[] {
    if (this.typeLogic == this.FILTER_IN_LABEL) return [];
    return this.selectedTypes.map(t => t.codevt);
  }

  get selectedChannels(): string[] {
    return rawChannels.filter(ch => (Number(this.planForm.value['channelBudget' + ch.id]) || 0) !== 0).map(ch => ch.id.toString());
  }

  get selectedChannelBudgets(): number[] {
    return this.selectedChannels.map(ch => Number(this.planForm.value['channelBudget' + ch]) || 0);
  }

  set selectedChannelBudgets(value: number[]) {
    const channels = this.selectedChannels;
    for (let idx in value) {
      let ch = channels[idx];
      let budget = value[idx];
      this.planForm.get('channelBudget' + ch).setValue(budget);
    }
  }

  get allChannelBudgets(): number[] {
    return rawChannels.map(ch => Number(this.planForm.value['channelBudget' + ch.id.toString()]) || 0);
  }

  set allChannelBudgets(value: number[]) {
    const channels = rawChannels.map(ch => ch.id.toString());
    for (let idx in value) {
      let ch = channels[idx];
      let budget = value[idx];
      this.planForm.get('channelBudget' + ch).setValue(budget);
    }
  }

  get selectedDiscount(): number {
    return -(Number(this.planForm.value.discount) || 0);
  }

  get selectedForcedGrossBudget(): number {
    return Number(this.planForm.value['grossBudget']) || 0;
  }

  set selectedForcedGrossBudget(value: number) {
    this.planForm.get('grossBudget').setValue(value);
  }

  get selectedForcedNetBudget(): number {
    return Number(this.planForm.value['netBudget']) || 0;
  }

  set selectedForcedNetBudget(value: number) {
    this.planForm.get('netBudget').setValue(value);
  }

  get selectedGrossBudget(): number {
    return Math.round(this.selectedNetBudget / (1 - this.selectedDiscount / 100));
  }

  get selectedNetBudget(): number {
    return Math.round(this.selectedChannelBudgets.reduce((p, c) => p + c, 0));
  }

  get selectedOptimization(): string {
    return this.planForm.get('optimizationMode')?.value;
  }

  get selectedReset(): boolean {
    return this.planForm.get('allocationMode')?.value == 0;
  }

  get isValid(): boolean {
    if (this.selectedStartDate > this.selectedEndDate) return false;

    if (new Set(this.selectedDurations).size !== this.selectedDurations.length) return false;

    if (this.selectedDurationsWeight.reduce((a, c) => Number(a) + Number(c), 0) != 100) return false;

    if (this.selectedDayPartsWeight.reduce((a, c) => Number(a) + Number(c), 0) != 100) return false;

    if (this.selectedChannels.length == 0) return false;

    if (this.selectedChannelBudgets.filter(b => b < 0).length) return false;

    if (this.selectedChannelBudgets.reduce((a, c) => Number(a) + Number(c), 0) <= 0) return false;

    return true;
  }

  private explodeScreenTypes(types: any[]): any[] {
    return [...types, ...types.filter(t => t.children).reduce((acc, t) => acc.concat(this.explodeScreenTypes(t.children)), [])];
  }

  private componentDestroyed$: Subject<void> = new Subject();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<TvPlanValuationDialogComponent>,
    private screenService: ScreenService,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {
    this.planAlloc = data.alloc;
    this.hasUnsavedSpots = data.hasUnsavedSpots;
    this.filteredCampaigns = of(data.campaigns);
    this.startCampaignDate = data.startCampaignDate;
    this.endCampaignDate = data.endCampaignDate;
    for (let idx in rawFormats) rawFormats[idx].visibility = false;
    for (let idx in this.planAlloc.durations) {
      rawFormats[idx].duration = this.planAlloc.durations[idx];
      rawFormats[idx].weight = this.planAlloc.durationsWeight[idx];
      rawFormats[idx].visibility = true;
    }
    for (let idx in rawDayParts) {
      rawDayParts[idx].weight = 0;
      for (let idx2 in this.planAlloc.dayparts)
        if (rawDayParts[idx].code == this.planAlloc.dayparts[idx2]) {
          rawDayParts[idx].weight = this.planAlloc.daypartsWeight[idx2];
          break;
        }
    }
    let budgets = [];
    budgets['2'] = 0;
    budgets['3'] = 0;
    budgets['5'] = 0;
    for (let idx in this.planAlloc.channels)
      budgets[this.planAlloc.channels[idx].toString()] = Math.round(this.planAlloc.channelsBudget[idx]);
    if (this.planAlloc.filterIn.length) {
      this.typeLogic = this.FILTER_IN_LABEL;
    }
    if (this.planAlloc.filterOut.length) {
      this.typeLogic = this.FILTER_OUT_LABEL;
    }

    let controls = {
      campaign: [data.campaigns[0]],
      startDate: this.planAlloc.startDate,
      endDate: this.planAlloc.endDate,
      discount: [-this.planAlloc.discountRate],
      allocationMode: [this.planAlloc.noreset ? '1' : '0'],
      optimizationMode: [this.planAlloc.optimization || 'cgrp'],
    };

    this.rawWaves = [];
    for (let idx in this.planAlloc.waves) {
      controls['waveStartDate' + idx] = [this.planAlloc.waves[idx].startDate];
      controls['waveEndDate' + idx] = [this.planAlloc.waves[idx].endDate];
      this.rawWaves = [...this.rawWaves, { index: idx }];
    }

    for (let idx in rawFormats) {
      controls['format' + idx] = [rawFormats[idx].duration];
      controls['formatWeight' + idx] = [rawFormats[idx].weight];
    }

    for (let idx in rawDayParts) controls['dayPart' + rawDayParts[idx].code] = [rawDayParts[idx].weight];

    for (let idx in budgets) controls['channelBudget' + idx] = [budgets[idx]];

    this.planForm = this.fb.group(controls);
  }

  ngOnInit(): void {
    this.initValue();

    this.onInvalidateTotalBudget();

    this.screenService
      .getScreenTypes()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((data: []) => {
        this.screenTypes = data;
        let hash = [];
        for (let item of this.explodeScreenTypes(data).filter(d => d['codevt'])) {
          hash[item['codevt']] = item;
        }
        let filter = [...this.planAlloc.filterIn, ...this.planAlloc.filterOut];
        this.selectedTypes = filter.filter(codevt => hash[codevt]).map(codevt => hash[codevt]);
      });

    // fix buggy propagation of the enter key to one of the add button
    document.addEventListener(
      'keydown',
      e => {
        const target = e.target as HTMLElement;
        if (target.tagName == 'BUTTON') return;
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
        }
      },
      { passive: false }
    );
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next();
    this.componentDestroyed$.unsubscribe();
  }

  /**
   * Close dialog with parameter
   * @param planAlloc
   */
  close(planAlloc: PlanAlloc = null, mustSavePlan: boolean = false): void {
    this.dialogRef.close({
      planAlloc,
      mustSavePlan,
    });
  }

  /**
   * Init control validators
   */
  private initValue(): void {
    this.planForm.addControl('campaign', this.fb.control(null, [Validators.required]));

    this.planForm.addControl('startDate', this.fb.control(null, [Validators.required, checkedMinMaxDateValidator]));
    this.planForm.addControl('endDate', this.fb.control(null, [Validators.required, checkedMinMaxDateValidator]));

    for (let idx = 0; idx < this.MAX_WAVES_COUNT; idx++) {
      this.planForm.addControl('waveStartDate' + idx, this.fb.control(null, []));
      this.planForm.addControl('waveEndDate' + idx, this.fb.control(null, []));
    }

    for (let b of rawChannelBudgets) this.planForm.addControl('channelBudget' + b.id.toString(), this.fb.control(null, []));

    for (let d of rawDayParts) this.planForm.addControl('daypart' + d.code, this.fb.control(null, []));

    for (let idx of rawFormats) {
      this.planForm.addControl('format' + idx, this.fb.control(null, []));
      this.planForm.addControl('formatWeight' + idx, this.fb.control(null, []));
    }

    this.planForm.addControl('discount', this.fb.control(null, [Validators.required]));

    this.planForm.addControl('grossBudget', this.fb.control(null, []));
    this.planForm.addControl('netBudget', this.fb.control(null, []));

    this.planForm.addControl('allocationMode', this.fb.control(null, []));

    this.planForm.addControl('optimizationMode', this.fb.control(null, []));
  }

  onCancel(): void {
    this.close();
  }

  onValidate(mustSavePlan: boolean = false): void {
    if (this.planForm.valid) {
      this.planAlloc.campaignNum = this.selectedCampaign.cpNum;
      this.planAlloc.campaignYear = this.selectedCampaign.cpYear;
      this.planAlloc.targetId = this.planAlloc.targetId ?? this.selectedCampaign.mainTarget;
      this.planAlloc.priceCode = this.selectedCampaign.priceCode;
      this.planAlloc.startDate = this.selectedStartDate;
      this.planAlloc.endDate = this.selectedEndDate;
      this.planAlloc.waves = this.selectedWaves;
      this.planAlloc.channels = this.selectedChannels;
      this.planAlloc.channelsBudget = this.selectedChannelBudgets;
      this.planAlloc.dayparts = this.selectedDayPartsCode;
      this.planAlloc.daypartsWeight = this.selectedDayPartsWeight;
      this.planAlloc.discountRate = this.selectedDiscount;
      this.planAlloc.durations = this.selectedDurations;
      this.planAlloc.durationsWeight = this.selectedDurationsWeight;
      this.planAlloc.filterIn = this.selectedFilterIn;
      this.planAlloc.filterOut = this.selectedFilterOut;
      this.planAlloc.optimization = this.selectedOptimization;
      this.planAlloc.noreset = !this.selectedReset;
      this.close(this.planAlloc, mustSavePlan);
    } else {
      this.snackBar.open('Validation error', null, {
        duration: 3000,
        horizontalPosition: 'end',
        verticalPosition: 'top',
        panelClass: ['error-snack'],
      });
    }
  }

  onAddWave(): void {
    if (this.rawWaves.length >= this.MAX_WAVES_COUNT) {
      return;
    }

    const idx = this.rawWaves.length;
    this.rawWaves = [...this.rawWaves, { index: idx }];
    this.planForm.get('waveStartDate' + idx).setValue(this.selectedStartDate);
    this.planForm.get('waveEndDate' + idx).setValue(this.selectedEndDate);
  }

  onRemoveWave(index): void {
    for (let idx = index; idx < this.rawWaves.length - 1; idx++) {
      this.rawWaves[idx] = this.rawWaves[idx + 1];
      this.planForm.get('waveStartDate' + idx).setValue(this.planForm.value['waveStartDate' + (idx + 1)]);
      this.planForm.get('waveEndDate' + idx).setValue(this.planForm.value['waveEndDate' + (idx + 1)]);
    }
    this.rawWaves = this.rawWaves.slice(0, -1);
  }

  onAddFormat(): void {
    let weight = 0;
    for (let idx in rawFormats) {
      if (rawFormats[idx].visibility) {
        weight += rawFormats[idx].weight;
        continue;
      }
      rawFormats[idx].duration = 0;
      rawFormats[idx].visibility = true;
      rawFormats[idx].weight = 100 - weight;
      this.planForm.get('format' + idx).setValue(rawFormats[idx].duration);
      this.planForm.get('formatWeight' + idx).setValue(rawFormats[idx].weight);
      return;
    }
  }

  onRemoveFormat(rIdx: number): void {
    for (let idx = rIdx; idx < rawFormats.length - 1; idx++) {
      rawFormats[idx].duration = this.planForm.get('format' + (idx + 1)).value;
      rawFormats[idx].visibility = rawFormats[idx + 1].visibility;
      rawFormats[idx].weight = this.planForm.get('formatWeight' + (idx + 1)).value;
    }
    rawFormats[rawFormats.length - 1].visibility = false;

    // update component values in order to pass validation
    let removedId = rawFormats.filter(f => f.visibility).length;
    rawFormats[removedId].duration = 1;
    rawFormats[removedId].weight = 0;

    if (removedId == 1) {
      rawFormats[0].weight = 100;
    }

    for (let idx in rawFormats) {
      if (rawFormats[idx].visibility || Number(idx) == rIdx) {
        this.planForm.get('format' + idx).setValue(rawFormats[idx].duration);
        this.planForm.get('formatWeight' + idx).setValue(rawFormats[idx].weight);
      }
    }
  }

  onInvalidateTotalBudget() {
    const netBudget = this.selectedNetBudget;
    const grossBudget = Math.round(netBudget / (1 - this.selectedDiscount / 100));
    // compute the net first which is the sum of channel budgets
    this.selectedForcedNetBudget = netBudget;
    // the gross is computed from the net, using the discount rate
    this.selectedForcedGrossBudget = grossBudget;
  }

  onInvalidateGrossBudget() {
    const netBudget = this.selectedForcedNetBudget;
    this.selectedForcedGrossBudget = Math.round(netBudget / (1 - this.selectedDiscount / 100));
  }

  onInvalidateNetBudget() {
    const grossBudget = this.selectedForcedGrossBudget;
    this.selectedForcedNetBudget = Math.round(grossBudget * (1 - this.selectedDiscount / 100));
  }

  prorata(repart, total) {
    const totalRepart = repart.reduce((a, v) => a + v, 0);
    if (totalRepart == 0) {
      return repart.map(r => Math.floor((1 * total) / repart.length));
    } else {
      return repart.map(r => Math.floor((r * total) / totalRepart));
    }
  }

  onInvalidateChannelBudgets() {
    const totalNetBudget = this.selectedForcedNetBudget;
    let repartition = this.selectedChannelBudgets;

    if (repartition.length == 0) {
      this.allChannelBudgets = this.prorata(this.allChannelBudgets, totalNetBudget);
    } else {
      this.selectedChannelBudgets = this.prorata(this.selectedChannelBudgets, totalNetBudget);
    }
  }

  onToggleTypeLogic() {
    this.typeLogic = this.typeLogic == this.FILTER_IN_LABEL ? this.FILTER_OUT_LABEL : this.FILTER_IN_LABEL;
  }

  onOpenTypeDialog() {
    const dialogRef = this.dialog.open(ScreenTypeComponent, {
      data: {
        screenType: this.screenTypes,
        selectedType: this.selectedTypes,
        screenTypeToInsert: this.screenTypeToInsert,
        screenTypeToDelete: this.screenTypeToDelete,
      },
      panelClass: 'tv-dialog-container',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(selectedTypes => {
        if (this.data.isCreating) {
          this.selectedTypes = selectedTypes;
        }
      });
  }

  onRemoveType(type): void {
    this.selectedTypes.forEach(element => {
      if (type === element) {
        const indexType = this.selectedTypes.indexOf(element);
        element.isSelected = false;
        this.selectedTypes.splice(indexType, 1);
      }
    });

    this.screenTypeToDelete.push(type);
  }
}
